// ui/createCardPlayDetectorOrReduced.js: M5 core-game-polish C4.
//
// Per-snapshot detector that surfaces newly-played cards as `play`
// events so the canvas layer can drive a slide animation from the
// player's hand area to the trick slot. Mirrors the shape of
// trick-resolution-detector.js so a future audit can verify all
// snapshot-delta detectors follow the same contract.
//
// What it does NOT do:
//   • doesn't drive a rAF loop. Owners (useCardPlayAnimation) advance
//     the clock and consume the event's `durationMs` field.
//   • doesn't compute layout positions. The renderer turns
//     {seat, card_id, durationMs} into screen coords using the same
//     layout helpers it already owns.
//   • doesn't dedupe across mount/unmount. One detector instance per
//     view mount, same lifecycle as createTrickResolutionDetector.
//
// Reduced-motion: the *OrReduced sibling returns events with
// durationMs=0 so callers can treat reduced/full alike, matching the
// easing.js sibling pattern (Decision B1).

/**
 * @typedef {{
 *   seat: 1|2|3|4,
 *   card_id: string,
 *   durationMs: number,
 * }} CardPlayEvent
 */

const DEFAULT_DURATION_MS = 320;

function normalizePlays(snapshot) {
  const raw = snapshot?.trick_plays;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.values(raw);
}

/**
 * Build a detector that emits one event per newly-added entry in
 * `snapshot.trick_plays`. Across a hand reset (count drops back to 0)
 * the internal high-water mark resets so the next trick fires events
 * fresh.
 *
 * @param {{ durationMs?: number }} [opts]
 */
export function createCardPlayDetector(opts = {}) {
  const durationMs = opts.durationMs ?? DEFAULT_DURATION_MS;
  let highWaterMark = 0;

  /**
   * @param {any} snapshot
   * @returns {CardPlayEvent[]}
   */
  function detect(snapshot) {
    if (!snapshot) return [];
    const plays = normalizePlays(snapshot);
    // Trick reset (count shrank): start fresh from the new count so a
    // hand that arrives mid-trick doesn't replay history.
    if (plays.length < highWaterMark) {
      highWaterMark = plays.length;
    }
    if (plays.length === highWaterMark) return [];
    const newOnes = plays.slice(highWaterMark);
    highWaterMark = plays.length;
    return newOnes
      .filter((p) => p && p.card_id != null && p.seat != null)
      .map((p) => ({
        seat: p.seat,
        card_id: p.card_id,
        durationMs,
      }));
  }

  function reset() { highWaterMark = 0; }

  return { detect, reset };
}

/**
 * Reduced-motion sibling. Same API as createCardPlayDetector but every
 * emitted event has durationMs=0 so the caller can collapse the slide
 * to an immediate snap with no branching at the render site.
 *
 * @param {{ durationMs?: number }} [_opts]
 */
export function createCardPlayDetectorOrReduced(_opts) {
  const inner = createCardPlayDetector({ durationMs: 0 });
  return inner;
}

export const _internals = {
  DEFAULT_DURATION_MS,
  normalizePlays,
};
